(function (createShippingProtection) {
  const api = createShippingProtection();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    api.bootstrap();
  }
})(function () {
  const CONFIG_ID = 'ShippingProtectionConfig';
  const STATE_KEY = 'ella:shipping-protection:state';
  const PENDING_ADD_KEY = 'ella:shipping-protection:pending-add';
  const PENDING_ADD_MAX_AGE = 5 * 60 * 1000;
  const DEBOUNCE_TIME = 120;

  class ShippingProtectionError extends Error {
    constructor(code, message) {
      super(message);
      this.code = code;
    }
  }

  function normalizeId(value) {
    return value == null ? '' : String(value);
  }

  function isProtectionLine(item, productId) {
    return normalizeId(item?.product_id) === normalizeId(productId);
  }

  function getPhysicalSubtotal(cart, productId) {
    return (cart?.items || []).reduce((subtotal, item) => {
      if (isProtectionLine(item, productId) || item.requires_shipping !== true) return subtotal;
      return subtotal + Number(item.final_line_price || 0);
    }, 0);
  }

  function getPhysicalQuantity(cart, productId) {
    return (cart?.items || []).reduce((quantity, item) => {
      if (isProtectionLine(item, productId) || item.requires_shipping !== true) return quantity;
      return quantity + Number(item.quantity || 0);
    }, 0);
  }

  function hasProtection(cart, productId) {
    return (cart?.items || []).some((item) => isProtectionLine(item, productId));
  }

  function normalizeTiers(tiers) {
    const normalized = (tiers || [])
      .map((tier) => ({
        min: Number(tier.min),
        max: Number(tier.max),
        price: Number(tier.price),
      }))
      .sort((left, right) => left.min - right.min);

    if (normalized.length === 0) {
      throw new ShippingProtectionError('configuration', 'No shipping protection tiers are configured.');
    }

    normalized.forEach((tier, index) => {
      if (![tier.min, tier.max, tier.price].every(Number.isInteger) || tier.min < 0 || tier.max < tier.min || tier.price <= 0) {
        throw new ShippingProtectionError('configuration', 'A shipping protection tier is invalid.');
      }

      if (index > 0 && normalized[index - 1].max >= tier.min) {
        throw new ShippingProtectionError('configuration', 'Shipping protection tiers overlap.');
      }
    });

    return normalized;
  }

  function resolveTier(tiers, subtotal) {
    return tiers.find((tier) => subtotal >= tier.min && subtotal <= tier.max) || null;
  }

  function resolveVariant(variants, price) {
    const matches = (variants || []).filter((variant) => variant.available === true && Number(variant.price) === Number(price));

    if (matches.length !== 1) {
      throw new ShippingProtectionError('variant', 'The configured shipping protection price must match one available variant.');
    }

    return matches[0];
  }

  function buildNormalizationUpdates(cart, productId, desiredVariantId) {
    const updates = {};
    let keptDesiredLine = false;

    (cart?.items || []).forEach((item) => {
      if (!isProtectionLine(item, productId)) return;

      if (!keptDesiredLine && normalizeId(item.variant_id) === normalizeId(desiredVariantId)) {
        keptDesiredLine = true;
        if (Number(item.quantity) !== 1) updates[item.key] = 1;
        return;
      }

      updates[item.key] = 0;
    });

    return {
      hasDesiredLine: keptDesiredLine,
      updates,
    };
  }

  function parseConfiguration() {
    const element = document.getElementById(CONFIG_ID);
    if (!element) return null;

    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      return {
        enabled: true,
        productId: null,
        productHandle: '',
        tiers: [],
        strings: {},
      };
    }
  }

  class ShippingProtectionManager {
    constructor(configuration) {
      this.configuration = configuration;
      this.productId = normalizeId(configuration.productId);
      this.productHandle = configuration.productHandle || '';
      this.strings = configuration.strings || {};
      this.queue = Promise.resolve();
      this.pendingAdd = false;
      this.pendingSource = 'initial';
      this.debounceTimer = null;
      this.lastCart = null;
      this.productPromise = null;
      this.failureCount = 0;
      this.internalMutation = false;

      try {
        if (!this.productId || !this.productHandle) {
          throw new ShippingProtectionError('configuration', 'A shipping protection product is required.');
        }
        this.tiers = normalizeTiers(configuration.tiers);
        this.configurationError = null;
      } catch (error) {
        this.tiers = [];
        this.configurationError = error;
      }
    }

    init() {
      this.bindEvents();
      this.schedule('initial');
    }

    bindEvents() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          const source = event?.source || '';
          const isAdd = source !== 'cart-items-component' && source !== 'cart-drawer-items';
          this.schedule('cart-add', isAdd);
        });
      }

      document.addEventListener('cart:updated', (event) => {
        if (event?.detail?.shippingProtectionInternal) return;
        this.schedule('cart-change');
      });
      document.addEventListener('DiscountUpdateEvent', () => this.schedule('discount'));
      document.addEventListener('discount:update', () => this.schedule('discount'));
      document.addEventListener('GiftWrapUpdateEvent', () => this.schedule('gift-wrap'));
      document.addEventListener('shopify:section:load', () => this.schedule('section-load'));
      window.addEventListener('pageshow', () => this.schedule('pageshow'));

      document.addEventListener(
        'submit',
        (event) => {
          const action = event.target?.action || '';
          if (action.includes('/cart/add')) this.markPendingAdd();
        },
        true
      );

      document.addEventListener(
        'click',
        (event) => {
          const link = event.target?.closest?.('a[href*="/cart/add"]');
          const reorder = event.target?.closest?.('[data-order-again]');
          if (link || reorder) this.markPendingAdd();
        },
        true
      );
    }

    schedule(source, isAdd = false) {
      this.pendingAdd = this.pendingAdd || isAdd;
      this.pendingSource = source;
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = window.setTimeout(() => {
        const allowOptOutReset = this.pendingAdd;
        const nextSource = this.pendingSource;
        this.pendingAdd = false;
        this.queue = this.queue.then(() => this.sync(nextSource, allowOptOutReset));
      }, DEBOUNCE_TIME);
    }

    async sync(source, allowOptOutReset) {
      try {
        const cart = await this.fetchCart();
        const physicalQuantity = getPhysicalQuantity(cart, this.productId);

        if (Number(cart.item_count) === 0) {
          this.clearOptOut();
          this.clearPendingAdd();
          this.clearWarning();
          this.failureCount = 0;
          this.lastCart = cart;
          return;
        }

        let optOutState = this.readOptOut(cart.token);
        const customerRemovedProtection =
          source === 'cart-change' &&
          this.lastCart &&
          hasProtection(this.lastCart, this.productId) &&
          !hasProtection(cart, this.productId) &&
          !this.internalMutation;

        if (customerRemovedProtection) {
          optOutState = {
            optedOut: true,
            token: cart.token,
            physicalQuantity,
          };
          this.writeOptOut(optOutState);
        }

        const pendingAddSucceeded = this.consumePendingAdd(cart, physicalQuantity);
        if (optOutState.optedOut && !customerRemovedProtection && (allowOptOutReset || pendingAddSucceeded)) {
          this.clearOptOut();
          optOutState = { optedOut: false };
        }

        if (cart.currency !== 'USD') {
          await this.removeProtection(cart);
          this.updateStoredQuantity(optOutState, cart, physicalQuantity);
          this.clearWarning();
          this.failureCount = 0;
          this.lastCart = await this.fetchCart();
          return;
        }

        if (this.configurationError) {
          throw this.configurationError;
        }

        const subtotal = getPhysicalSubtotal(cart, this.productId);
        const tier = resolveTier(this.tiers, subtotal);

        if (!tier) {
          await this.removeProtection(cart);
          this.updateStoredQuantity(optOutState, cart, physicalQuantity);
          this.clearWarning();
          this.failureCount = 0;
          this.lastCart = await this.fetchCart();
          return;
        }

        if (optOutState.optedOut) {
          this.updateStoredQuantity(optOutState, cart, physicalQuantity);
          this.clearWarning();
          this.failureCount = 0;
          this.lastCart = cart;
          return;
        }

        const product = await this.fetchProduct();
        const variant = resolveVariant(product.variants, tier.price);
        const finalCart = await this.normalizeProtection(cart, variant.id);
        this.clearWarning();
        this.failureCount = 0;
        this.lastCart = finalCart;
      } catch (error) {
        this.handleError(error);
      }
    }

    updateStoredQuantity(state, cart, physicalQuantity) {
      if (!state.optedOut) return;
      this.writeOptOut({
        optedOut: true,
        token: cart.token,
        physicalQuantity,
      });
    }

    async fetchCart() {
      const response = await fetch(`${window.routes.cart_url}.js`, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      const data = await response.json();
      if (!response.ok) throw new ShippingProtectionError('network', data.description || data.message || 'Unable to load cart.');
      return data;
    }

    fetchProduct() {
      if (this.productPromise) return this.productPromise;

      const root = String(window.routes.root_url || '/').replace(/\/?$/, '/');
      const productUrl = `${root}products/${encodeURIComponent(this.productHandle)}.js`;
      this.productPromise = fetch(productUrl, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      })
        .then(async (response) => {
          const data = await response.json();
          if (!response.ok) {
            throw new ShippingProtectionError('network', data.description || data.message || 'Unable to load shipping protection product.');
          }
          if (normalizeId(data.id) !== this.productId) {
            throw new ShippingProtectionError('configuration', 'The shipping protection product configuration is stale.');
          }
          return data;
        })
        .catch((error) => {
          this.productPromise = null;
          throw error;
        });

      return this.productPromise;
    }

    getSectionIds() {
      const ids = new Set(['cart-icon-bubble']);
      document.querySelectorAll('cart-items-component[data-section-id], cart-drawer-items[data-section-id], cart-drawer[data-section-id]').forEach((element) => {
        if (element.dataset.sectionId) ids.add(element.dataset.sectionId);
      });
      return Array.from(ids);
    }

    getMutationBody(body) {
      return {
        ...body,
        sections: this.getSectionIds(),
        sections_url: window.location.pathname,
      };
    }

    async postCart(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(this.getMutationBody(body)),
      });
      const data = await response.json();
      if (!response.ok || data.status || data.errors) {
        throw new ShippingProtectionError('network', data.description || data.message || data.errors || 'Unable to update cart.');
      }
      return data;
    }

    async removeProtection(cart) {
      const updates = {};
      (cart.items || []).forEach((item) => {
        if (isProtectionLine(item, this.productId)) updates[item.key] = 0;
      });

      if (Object.keys(updates).length === 0) return cart;

      this.internalMutation = true;
      try {
        const updatedCart = await this.postCart(window.routes.cart_update_url, { updates });
        this.renderCart(updatedCart.sections, updatedCart);
        return updatedCart;
      } finally {
        this.internalMutation = false;
      }
    }

    async normalizeProtection(cart, desiredVariantId) {
      this.internalMutation = true;

      try {
        let workingCart = cart;
        let sections = null;
        let normalization = buildNormalizationUpdates(workingCart, this.productId, desiredVariantId);

        if (!normalization.hasDesiredLine) {
          const addResult = await this.postCart(window.routes.cart_add_url, {
            items: [{ id: Number(desiredVariantId), quantity: 1 }],
          });
          sections = addResult.sections || null;
          workingCart = await this.fetchCart();
          normalization = buildNormalizationUpdates(workingCart, this.productId, desiredVariantId);
        }

        if (Object.keys(normalization.updates).length > 0) {
          workingCart = await this.postCart(window.routes.cart_update_url, {
            updates: normalization.updates,
          });
          sections = workingCart.sections || sections;
        } else if (!normalization.hasDesiredLine) {
          throw new ShippingProtectionError('network', 'Shipping protection was not added to the cart.');
        }

        if (!workingCart.items || workingCart.item_count == null) {
          workingCart = await this.fetchCart();
        }

        this.renderCart(sections, workingCart);
        return workingCart;
      } finally {
        this.internalMutation = false;
      }
    }

    renderCart(sections, cart) {
      if (sections) {
        const mainCart = document.querySelector('cart-items-component');
        const drawerCart = document.querySelector('cart-drawer-items');

        if (mainCart && typeof mainCart.updateSections === 'function') mainCart.updateSections(sections);
        if (drawerCart && typeof drawerCart.updateSections === 'function') drawerCart.updateSections(sections);
        this.renderStandaloneSection('cart-icon-bubble', sections['cart-icon-bubble']);
      }

      document.querySelectorAll('[data-cart-count]').forEach((element) => {
        element.textContent = Number(cart.item_count) >= 100 ? this.getCartMaximumLabel() : cart.item_count;
      });

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: {
            shippingProtectionInternal: true,
            item_count: cart.item_count,
          },
        })
      );
    }

    renderStandaloneSection(id, html) {
      if (!html) return;
      const current = document.getElementById(id);
      const nextDocument = new DOMParser().parseFromString(html, 'text/html');
      const next = nextDocument.getElementById(id);
      if (current && next) current.innerHTML = next.innerHTML;
    }

    getCartMaximumLabel() {
      return window.cartStrings?.item_99 || '99+';
    }

    readOptOut(cartToken) {
      try {
        const value = JSON.parse(window.localStorage.getItem(STATE_KEY) || 'null');
        if (value?.optedOut && normalizeId(value.token) === normalizeId(cartToken)) return value;
      } catch (error) {
        this.clearOptOut();
      }
      return { optedOut: false };
    }

    writeOptOut(state) {
      try {
        window.localStorage.setItem(STATE_KEY, JSON.stringify(state));
      } catch (error) {
        return;
      }
    }

    clearOptOut() {
      try {
        window.localStorage.removeItem(STATE_KEY);
      } catch (error) {
        return;
      }
    }

    markPendingAdd() {
      const cart = this.lastCart;
      const storedState = cart ? this.readOptOut(cart.token) : { optedOut: false };
      if (!storedState.optedOut) return;

      try {
        window.sessionStorage.setItem(
          PENDING_ADD_KEY,
          JSON.stringify({
            token: cart?.token || storedState.token,
            physicalQuantity: cart ? getPhysicalQuantity(cart, this.productId) : Number(storedState.physicalQuantity || 0),
            createdAt: Date.now(),
          })
        );
      } catch (error) {
        return;
      }
    }

    consumePendingAdd(cart, physicalQuantity) {
      let pending = null;
      try {
        pending = JSON.parse(window.sessionStorage.getItem(PENDING_ADD_KEY) || 'null');
      } catch (error) {
        this.clearPendingAdd();
        return false;
      }

      if (!pending) return false;
      this.clearPendingAdd();

      if (Date.now() - Number(pending.createdAt || 0) > PENDING_ADD_MAX_AGE) return false;
      const sameCart = normalizeId(pending.token) === normalizeId(cart.token);
      return (!sameCart && physicalQuantity > 0) || physicalQuantity > Number(pending.physicalQuantity || 0);
    }

    clearPendingAdd() {
      try {
        window.sessionStorage.removeItem(PENDING_ADD_KEY);
      } catch (error) {
        return;
      }
    }

    clearWarning() {
      document.querySelectorAll('[data-shipping-protection-warning]').forEach((element) => {
        element.hidden = true;
      });
    }

    showWarning(message) {
      document.querySelectorAll('[data-shipping-protection-warning]').forEach((element) => {
        element.textContent = message;
        element.hidden = false;
      });
    }

    handleError(error) {
      const code = error instanceof ShippingProtectionError ? error.code : 'network';
      const message =
        code === 'configuration'
          ? this.strings.configurationError
          : code === 'variant'
            ? this.strings.variantError
            : this.strings.error;

      this.showWarning(message || 'Shipping protection could not be updated. You can still check out.');

      if (code === 'network' && this.failureCount < 2) {
        this.failureCount += 1;
        window.setTimeout(() => this.schedule('retry'), 1200);
      }
    }
  }

  function bootstrap() {
    const configuration = parseConfiguration();
    if (!configuration?.enabled) return;
    new ShippingProtectionManager(configuration).init();
  }

  return {
    ShippingProtectionError,
    buildNormalizationUpdates,
    getPhysicalQuantity,
    getPhysicalSubtotal,
    isProtectionLine,
    normalizeTiers,
    resolveTier,
    resolveVariant,
    bootstrap,
  };
});
